import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Service, Transaction } from 'types/rest/assetCatalogTypes';
import { BlueprintDraft } from '../../../store/blueprints/blueprintsTypes';
import ServicePicker from '../../../components/ui/servicePicker/ServicePicker';
import { validateBlueprintDraft } from '../../../store/blueprints/blueprintsValidation';
import BlueprintMetaFields from './BlueprintMetaFields';
import BlazeTable from '../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from '../../../components/blazeTable/BlazeTable.types';
import VirtualServiceTypeIcon from '../../../components/icons/VirtualServiceTypeIcon';
import SimpleTagList from '../../../components/ui/tags/SimpleTagList';
import { getColoredHttpMethod } from '../../../utils/commonUtil';
import { getErrorMessage } from '../../../utils/miscUtil';
import { showToastNotification } from '../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../store/ui/toasts/toastsTypes';
import acApi from '../../../axios/ac/acApi';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import styles from './BlueprintFormModal.module.css';

interface Props {
  initialDraft: BlueprintDraft;
  canPublishAccount: boolean;
  onSave: (draft: BlueprintDraft, serviceId: number) => void;
  onCancel: () => void;
}

const PAGE_SIZE = 10;
const TOTAL_STEPS = 2;

const BlueprintFormModal = (props: Props) => {
  const { t } = useTranslation('blueprints');
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<BlueprintDraft>(props.initialDraft);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2>(1);

  // Backend-paginated list of selectable transactions; only the current page is held here.
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTxns, setSelectedTxns] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); // 1-based page number
  const [isLoading, setIsLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  const update = (patch: Partial<BlueprintDraft>) => setDraft(d => ({ ...d, ...patch }));

  // Only fetch the transaction list once the user reaches the selection step.
  useEffect(() => {
    if (step !== 2) return undefined;
    let cancelled = false;
    setIsLoading(true);
    const params = {
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      sort: '-id',
      ...(selectedService?.id != null && { serviceId: selectedService.id }),
    };
    acApi.transactions.getTransactions({ params })
      .then(response => {
        if (cancelled) return;
        setTransactions(response.data.result ?? []);
        setTotal(response.data.total ?? 0);
      })
      .catch(e => {
        if (cancelled) return;
        setTransactions([]);
        dispatch(showToastNotification(ToastType.Danger, 'Cannot load transactions', getErrorMessage(e)));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, step, dispatch, selectedService]);

  const goNext = () => {
    const validationError = validateBlueprintDraft(draft, t, { requireTransactions: false });
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep(2);
  };

  const goBack = () => {
    setError(null);
    setStep(1);
  };

  // The table runs in backend-pagination mode, so it can only report selections for the
  // page currently loaded. We keep the full selection "aside" in draft.transactionIds and
  // reconcile each change against the current page's ids — selections on other pages are
  // preserved, and per-page select-all only affects the visible page.
  const onSelectionChange = (items: Array<number | Transaction>) => {
    const pageIds = new Set(transactions.map(txn => txn.id).filter((id): id is number => id != null));
    const returnedIds = (items ?? [])
      .map(item => (typeof item === 'number' ? item : item.id))
      .filter((id): id is number => id != null);
    const selectedOnPage = new Set(returnedIds.filter(id => pageIds.has(id)));
    const offPage = selectedTxns.filter(txn => txn.id != null && !pageIds.has(txn.id));
    const onPage = transactions.filter(txn => txn.id != null && selectedOnPage.has(txn.id));
    const next = [...offPage, ...onPage];
    setSelectedTxns(next);
    update({ transactionIds: next.map(txn => txn.id as number) });
  };

  const handleSave = () => {
    const validationError = validateBlueprintDraft(draft, t);
    if (validationError) {
      setError(validationError);
      return;
    }
    const serviceIds = Array.from(
      new Set(selectedTxns.map(txn => txn.serviceId).filter((sid): sid is number => sid != null)),
    );
    if (serviceIds.length !== 1) {
      setError(t('form.singleServiceError'));
      return;
    }
    setError(null);
    props.onSave(draft, serviceIds[0]);
  };

  const columns: BlazeTableColDef[] = [
    {
      header: 'Name',
      accessor: 'name',
      isSortable: false,
      width: 280,
      cellComponent: (txn: Transaction) => (
        <div className="d-flex align-items-center">
          <VirtualServiceTypeIcon type={txn.type} className={'mr-3 align-middle'} />
          {txn.name}
        </div>
      ),
    },
    {
      header: 'Method',
      accessor: 'method',
      isSortable: false,
      width: 100,
      cellComponent: (txn: Transaction) =>
        txn.type === 'MESSAGING'
          ? '-'
          : getColoredHttpMethod('method' in txn.dsl.requestDsl ? (txn.dsl.requestDsl.method as any) : '-'),
    },
    {
      header: 'Request URL',
      accessor: 'url',
      isSortable: false,
      cellComponent: (txn: Transaction) =>
        txn.type === 'MESSAGING' ? (
          '-'
        ) : (
          <code>{'url' in txn.dsl.requestDsl ? txn.dsl.requestDsl.url.matchingValue : '-'}</code>
        ),
    },
    {
      header: 'Response Status',
      accessor: 'status',
      isSortable: false,
      width: 130,
      cellComponent: (txn: Transaction) =>
        txn.type === 'MESSAGING' ? (
          '-'
        ) : (
          <span>{'status' in txn.dsl.responseDsl ? txn.dsl.responseDsl.status : '-'}</span>
        ),
    },
    {
      header: 'Service',
      accessor: 'serviceName',
      isSortable: false,
      width: 180,
      cellComponent: (txn: Transaction) => <span>{txn.serviceName}</span>,
    },
    {
      header: 'Tags',
      accessor: 'tags',
      isSortable: false,
      width: 160,
      cellComponent: (txn: Transaction) => (
        <div className="picked">{txn.tags != null && <SimpleTagList tags={txn.tags} />}</div>
      ),
    },
  ];

  const tableOptions: BlazeTableOptions = {
    columnDef: columns,
    selectable: true,
    selectAllDropdown: false,
    showRowExpander: false,
    pageable: true,
    sortable: false,
    pageSize: PAGE_SIZE,
    tableName: 'blueprint-available-transactions',
    noResultsText: t('form.noTransactions'),
    paginationOverride: { activePage: page, pageSize: PAGE_SIZE, total },
  };

  const stepIndicator = (n: 1 | 2, label: string) => (
    <div className={clsx(styles.step, step === n && styles.stepActive)}>
      <span className={clsx(styles.stepNum, step === n && styles.stepNumActive)}>{n}</span>
      {label}
    </div>
  );

  return (
    <Modal show onHide={props.onCancel} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('form.createTitle')}{' '}
          <span className="text-muted small ml-2">{t('form.stepLabel', { current: step, total: TOTAL_STEPS })}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.steps}>
          {stepIndicator(1, t('form.stepDetails'))}
          <span className={styles.stepSep} />
          {stepIndicator(2, t('form.stepTransactions'))}
        </div>

        {step === 1 && (
          <BlueprintMetaFields
            draft={draft}
            onChange={update}
            canPublishAccount={props.canPublishAccount}
            twoColumn
          />
        )}

        {step === 2 && (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <strong>{t('form.transactions')}</strong>
                <span className="badge badge-primary ml-2">
                  {t('form.selected', { count: draft.transactionIds.length })}
                </span>
              </div>
              <div style={{ width: 280 }}>
                <ServicePicker
                  selectHandler={(svc: Service | null) => {
                    setSelectedService(svc);
                    setPage(1);
                  }}
                  isSelectAll={true}
                  isCreateEnabled={false}
                  isMaintenanceEnabled={false}
                  preSelected={selectedService?.id}
                  maxHeight="230px"
                />
              </div>
            </div>
            <BlazeTable
              data={transactions}
              isLoading={isLoading}
              options={tableOptions}
              selectedItems={draft.transactionIds}
              onSelectionChange={onSelectionChange}
              onPageChange={p => setPage(p)}
            />
          </div>
        )}

        {error && <div className="text-danger mt-3">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        {step === 1 ? (
          <>
            <button type="button" className="btn btn-link" onClick={props.onCancel}>
              {t('form.cancel')}
            </button>
            <button type="button" data-test="bp-next" className="btn btn-primary" onClick={goNext}>
              {t('form.next')}
            </button>
          </>
        ) : (
          <>
            <button type="button" data-test="bp-back" className="btn btn-link" onClick={goBack}>
              {t('form.back')}
            </button>
            <button
              type="button"
              data-test="bp-save"
              className="btn btn-primary"
              disabled={draft.transactionIds.length === 0}
              onClick={handleSave}
            >
              {t('form.save')}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BlueprintFormModal;
